// Considered: ItemCardMolecule — rejected because it represents a channel item (stars, deploy button,
// use-case tags); TemplateCardMolecule represents a (schema + approach) template with count chips,
// Official/New pills, and channel provenance footer. See spec Reuse Audit (specs/add-sphere-overhaul.md).
import React from 'react';
import { useTranslation } from 'localization';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import type { TemplateApproach } from '../../../types/templateApproach';
import './TemplateCardMolecule.scss';

export interface TemplateCardMoleculeProps {
  template: TemplateApproach;
  /** Schema display name resolved from templateFor. Shown as "Creates a {schemaName}". */
  schemaName?: string;
  /** Schema glyph (icon class or Iconify id) for the header band. */
  schemaGlyph?: string | null;
  onClick: (template: TemplateApproach) => void;
  selected?: boolean;
  className?: string;
  'data-testid'?: string;
}

const TemplateCardMolecule: React.FC<TemplateCardMoleculeProps> = ({
  template,
  schemaName,
  schemaGlyph,
  onClick,
  selected = false,
  className,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();

  const glyphIcon = schemaGlyph ?? 'pi pi-box';

  return (
    <button
      type="button"
      className={`template-card${selected ? ' template-card--selected' : ''}${className ? ' ' + className : ''}`}
      onClick={() => onClick(template)}
      aria-pressed={selected}
      data-testid={dataTestId}
    >
      {/* Header band: glyph + name + "Creates a {schema}" + Official/New pills */}
      <div className="template-card__header">
        <span className={`template-card__glyph ${glyphIcon.startsWith('pi') ? glyphIcon : ''}`} aria-hidden="true" />
        <div className="template-card__header-text">
          <div className="template-card__name">{template.name}</div>
          {schemaName && (
            <div className="template-card__creates-label">
              {t('addSphere.dialog.template.createsA', { schemaName })}
            </div>
          )}
        </div>
        <div className="template-card__header-badges">
          {template.official && (
            <ChipAtom
              label={t('addSphere.dialog.template.official')}
              icon="pi pi-verified"
              className="template-card__badge template-card__badge--official"
            />
          )}
          {template.isNew && (
            <ChipAtom
              label={t('addSphere.dialog.template.new')}
              className="template-card__badge template-card__badge--new"
            />
          )}
        </div>
      </div>

      {/* Body: description + count chips */}
      <div className="template-card__body">
        <p className="template-card__description">{template.description}</p>
        <div className="template-card__count-chips">
          {template.childCount > 0 && (
            <ChipAtom
              label={t('addSphere.dialog.template.childCount', { count: template.childCount })}
              icon="pi pi-sitemap"
              className="template-card__count-chip"
            />
          )}
          {template.factorCount > 0 && (
            <ChipAtom
              label={t('addSphere.dialog.template.factorCount', { count: template.factorCount })}
              icon="pi pi-chart-bar"
              className="template-card__count-chip"
            />
          )}
          {template.metaspaceCount > 0 && (
            <ChipAtom
              label={t('addSphere.dialog.template.metaspaceCount', { count: template.metaspaceCount })}
              icon="pi pi-th-large"
              className="template-card__count-chip"
            />
          )}
        </div>
      </div>

      {/* Channel provenance footer — only shown when approach is sourced from a channel */}
      {template.channel && (
        <div className="template-card__footer">
          <span className="template-card__channel-dot" aria-hidden="true" />
          <span className="template-card__channel-name">
            {t('addSphere.dialog.template.fromChannel', { channelName: template.channel.name })}
          </span>
          {template.channel.subscribed && (
            <span className="template-card__subscribed">
              {t('addSphere.dialog.template.subscribed')}
            </span>
          )}
        </div>
      )}
    </button>
  );
};

export default TemplateCardMolecule;
